import React, { useState, useMemo } from "react";
import tinycolor from "tinycolor2";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import {
  buildBailoutPayload,
  captureBailout,
  hasCallbackNumber,
} from "../utils/bailout";
import { getAppointmentTheme } from "../utils/theme";
import {
  ErrorMessage,
  Field,
  Form,
  SimpleForm,
} from "../components/SimpleForm";
import { BookingStage } from "../constants/bookingStages";

const namePattern = /^[a-zA-ZÀ-ÿ\s\-. '&]+$/;

const required = (value) => (value ? undefined : "(required)");
const name = (value) =>
  required(value) ??
  (namePattern.test(value) ? undefined : "Name should not contain numbers");

const validate = (values) => ({
  fname: name(values.fname),
  lname: name(values.lname),
  phone:
    required(values.phone) ??
    (hasCallbackNumber(values.phone) ? undefined : "(invalid)"),
  email: required(values.email),
  comments: required(values.comments),
});

const SkipToForm = ({ appointment }) => {
  const [showThanks, setShowThanks] = useState(false);
  const [bailOutValues, setBailOutValues] = useState({});

  const dispatch = useDispatch();

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );

  const bailout = async (values) => {
    const payloadValues = buildBailoutPayload({
      appointment,
      customer: {
        fname: values.fname,
        lname: values.lname,
        phone: values.phone,
        email: values.email,
      },
      comments: values.comments,
      bailOutStep: appointment.previousStage || 0,
    });

    try {
      await captureBailout({
        appointment,
        payload: payloadValues,
        dispatch,
        updateAppointmentAction: updateAppointment,
      });
      dispatch(
        updateAppointment({
          updates: {
            bailed: true,
          },
        })
      );
      setBailOutValues(payloadValues);
      setShowThanks(true);
    } catch (error) {
      console.error("Error in bailout process:", error);
    }
  };

  const AreYouSure = () => {
    let message = "Let us know how we can help!";

    return (
      <div
        className="mb-5 p-4 rounded-xl text-sm"
        style={{
          backgroundColor: tinycolor(accentColor).setAlpha(0.15).toRgbString(),
          color: "#1f2937",
        }}
      >
        {message}
      </div>
    );
  };

  return (
    <div
      className="px-4 py-5 md:px-6 md:py-6 min-h-full font-roboto"
      style={{ background: gradientBackground }}
    >
      <div
        className="rounded-2xl bg-white p-5"
        style={{
          border: `2px solid ${accentColor}33`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {showThanks && appointment.stage > 0 ? (
          <div className="text-sm" style={{ whiteSpace: "pre-line", color: "#1f2937" }}>
            {appointment?.shopData?.bailOutThanks ?? (
              <>
                Thanks for visiting,{" "}
                {bailOutValues?.customer?.fname?.charAt(0).toUpperCase() +
                  bailOutValues?.customer?.fname?.slice(1)}
                ! We will reach out to you shortly to discuss your service
                needs. In the meantime, feel free to contact us at{" "}
                <a
                  href={`tel:${appointment?.shopData?.shopContact?.phone?.number}`}
                  style={{ color: accentColor }}
                >
                  {appointment?.shopData?.shopContact?.phone?.number}
                </a>
                <br />
                <br />
                <em>- Your {appointment.shopData?.shopName?.name} Team</em>
              </>
            )}
          </div>
        ) : (
          <SimpleForm
            initialValues={{
              fname: appointment?.customer?.fname || "",
              lname: appointment?.customer?.lname || "",
              phone: appointment?.customer?.phone || "",
              email: appointment?.customer?.email || "",
              comments: "",
            }}
            validate={validate}
            onSubmit={(values) => {
              bailout(values);
            }}
          >
            {({ touched, errors, handleSubmit }) => (
              <Form>
                <h2
                  className="text-lg font-bold mb-4"
                  style={{ color: "#1f2937" }}
                >
                  {appointment.service}
                </h2>

                <AreYouSure />

                <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6">
                  {/* Left column: text fields */}
                  <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-col">
                      <label
                        htmlFor="fname"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        First Name{" "}
                        <ErrorMessage
                          name="fname"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="fname"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="lname"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        Last Name{" "}
                        <ErrorMessage
                          name="lname"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="lname"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="phone"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        Phone Number{" "}
                        <ErrorMessage
                          name="phone"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="phone"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="email"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        Email Address{" "}
                        <ErrorMessage
                          name="email"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="email"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Right column: textarea */}
                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="comments"
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#1f2937" }}
                    >
                      Comments{" "}
                      <ErrorMessage
                        name="comments"
                        component="span"
                        className="text-red-500 text-sm font-normal"
                      />
                    </label>
                    <Field
                      name="comments"
                      as="textarea"
                      className="w-full box-border p-2 border-2 rounded-lg text-sm md:h-full min-h-[120px]"
                      placeholder="Car Make, Model, Year, etc."
                      style={{
                        border: `2px solid ${accentColor}66`,
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    className="cursor-pointer rounded-lg border-2 border-solid px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: accentColor,
                      color: accentColor,
                      boxShadow: "none",
                    }}
                    onClick={() => {
                      dispatch(
                        updateAppointment({
                          updates: {
                            stage: BookingStage.ServiceSelection,
                          },
                        })
                      );
                    }}
                  >
                    Change Service
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer rounded-lg px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-90 active:opacity-80 focus:outline-none"
                    style={{
                      backgroundColor: accentColor,
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "none",
                    }}
                    onClick={handleSubmit}
                  >
                    Send It!
                  </button>
                </div>
              </Form>
            )}
          </SimpleForm>
        )}
      </div>
    </div>
  );
};

export { SkipToForm };
